import React, { ReactElement, useEffect, useState } from 'react'
import { PrimaryButton } from './Button'
import Radom, { ICheckoutSession, PaymentSession, DonationLinkOrder, IFlatAndPercentFee, RefundPayerAddress } from '../state/Radom'
import { RadioButtonGroup } from './RadioButton'
import { InputLabel, TextInput, TextInputWithLabel } from './Input'
import NumberInput from './NumberInput'
import Switch from './Switch'
import Tooltip from './Tooltip'
import { RADOM_COLORS } from '../util/Constants'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import { errorToast, successToast, trimZeros } from '../util/Util'
import { getMethod } from '../util/Managed'

interface RefundProps {
  checkoutSession?: ICheckoutSession
  paymentSession?: PaymentSession
  donationLinkOrder?: DonationLinkOrder
  onRefund: () => void
}

// EEA country prefixes; refunds to banks outside these require the payer's address (SEPA).
const EEA_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IS',
  'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
])

const RefundForm = (props: RefundProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [isFullRefund, setIsFullRefund] = useState(true)
  const [chargeCustomerNetworkFee, setChargeCustomerNetworkFee] = useState(true)
  const [openBankingRefundFee, setOpenBankingRefundFee] = useState<IFlatAndPercentFee>({
    feePercent: '0.03',
    flatFeeCurrency: 'EUR',
    flatFeeAmount: '10'
  })

  const payment = props.checkoutSession?.payment?.managed ||
    props.checkoutSession?.payment?.managedTokenDelegatePayment ||
    props.checkoutSession?.payment?.openBanking ||
    props.paymentSession?.payment?.managed ||
    props.donationLinkOrder?.payment?.managed

  const currency = props.checkoutSession?.payment?.openBanking ? props.checkoutSession?.currency : getMethod(props.checkoutSession?.payment?.managed?.network || '', props.checkoutSession?.payment?.managed?.token).ticker

  if (!payment) {
    return <></>
  }

  const [refundAmount, setRefundAmount] = useState(payment.amount)
  const [recipientAddress, setRecipientAddress] = useState('')
  const [payerCity, setPayerCity] = useState('')
  const [payerCountry, setPayerCountry] = useState('')
  const [payerAddressLine1, setPayerAddressLine1] = useState('')
  const [payerPostalCode, setPayerPostalCode] = useState('')

  const openBankingData = props.checkoutSession?.payment?.openBanking?.openBankingPaymentData
  const senderName = openBankingData?.senderName ?? openBankingData?.payerFinancialAddress?.iban?.accountHolderName
  const senderIban = openBankingData?.senderIban ?? openBankingData?.payerFinancialAddress?.iban?.iban
  const senderIbanCountry = (senderIban ?? '').trim().slice(0, 2).toUpperCase()
  const requiresPayerAddress = !!props.checkoutSession?.payment?.openBanking &&
    senderIbanCountry.length === 2 &&
    !EEA_COUNTRY_CODES.has(senderIbanCountry)
  const isPayerAddressValid = payerCity.trim().length > 0 && /^[A-Z]{2}$/.test(payerCountry.trim())

  const submitRefund = async (): Promise<void> => {
    setIsLoading(true)

    let payerAddress: RefundPayerAddress | undefined
    if (requiresPayerAddress) {
      payerAddress = {
        city: payerCity.trim(),
        country: payerCountry.trim().toUpperCase()
      }
      if (payerAddressLine1.trim()) payerAddress.line1 = payerAddressLine1.trim()
      if (payerPostalCode.trim()) payerAddress.postalCode = payerPostalCode.trim()
    }

    Radom.refund({
      paymentSessionId: props.paymentSession?.id,
      checkoutSessionId: props.checkoutSession?.id,
      donationLinkOrderId: props.donationLinkOrder?.id,
      refundAmount,
      recipientAddress,
      chargeCustomerNetworkFee,
      payerAddress
    }).then(() => {
      successToast('Refund submitted')
      props.onRefund()
    }).catch(err => {
      const isPermissionError = err?.response?.status === 401 || err?.response?.status === 403
      errorToast(
        isPermissionError
          ? 'Write refunds permission is required to issue refunds'
          : err.reason || err.response?.data?.error || err.message
      )
    }).finally(() => {
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (isFullRefund) setRefundAmount(payment.amount)
  }, [isFullRefund])

  useEffect(() => {
    if (!props.checkoutSession?.payment?.openBanking) return
    Radom.getOrgFees()
      .then(fees => {
        if (fees.openBankingRefund) setOpenBankingRefundFee(fees.openBankingRefund)
      })
      .catch(err => console.error('Failed to load refund fees', err))
  }, [])

  const refundFeePercent = parseFloat((parseFloat(openBankingRefundFee.feePercent) * 100).toFixed(6))
  const refundFlatFee = parseFloat(openBankingRefundFee.flatFeeAmount)
  const openBankingFeeAmount = parseFloat(refundAmount || '0') * parseFloat(openBankingRefundFee.feePercent) + refundFlatFee
  const openBankingTotalAmount = parseFloat(refundAmount || '0') + openBankingFeeAmount
  const isUnsupportedOpenBankingCurrency = !!props.checkoutSession?.payment?.openBanking && currency?.toUpperCase() !== 'EUR'

  return <div style={{
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    // The app-wide body reset zeroes font-size; without an explicit baseline
    // here, every text node relying on inheritance renders invisible.
    fontSize: 14
  }}>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <InputLabel>Refund type</InputLabel>
      <RadioButtonGroup
        disabled={isLoading}
        labels={['Full refund', 'Partial refund']}
        selectedLabel={isFullRefund ? 'Full refund' : 'Partial refund'}
        onSelect={label => setIsFullRefund(label === 'Full refund')}
      />
    </div>

    {
      !isFullRefund &&
      <div style={{ position: 'relative' }}>
        <PaymentMethodDisplay
          style={{
            position: 'absolute',
            left: 5,
            top: 4,
            zIndex: 1,
            transform: 'scale(0.85)',
            pointerEvents: 'none'
          }}
          method={{ network: payment.network, token: payment.token }}
          excludeName />
        <NumberInput
          style={{ textIndent: 20 }}
          disabled={isLoading}
          autoFocus
          placeholder="Refund amount"
          min="0"
          autoComplete="off"
          value={refundAmount}
          fontSize="14"
          onUserInput={(e) => setRefundAmount(e)}
        />
      </div>
    }

    {
      !props.checkoutSession?.payment?.openBanking && <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 3 }}>
            <InputLabel>Charge contact network fee</InputLabel>
            <Tooltip message='If this is enabled, the gas fee to transfer the refund will be deducted from the refund amount.' />
          </div>
          <Switch
            checked={chargeCustomerNetworkFee}
            onClick={() => setChargeCustomerNetworkFee(!chargeCustomerNetworkFee)}
            disabled={isLoading} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Recipient address</InputLabel>
          <TextInput
            disabled={isLoading}
            autoFocus
            placeholder=''
            value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)} />
        </div>
      </>
    }

    {
      props.checkoutSession?.payment?.openBanking &&
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <InputLabel>Fee breakdown</InputLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 14 }}>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>
            <span>Refund amount</span>
            <span style={{ fontWeight: 600 }}>{parseFloat(refundAmount || '0').toFixed(2)} {props.checkoutSession?.currency}</span>
          </div>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>
            <span>Radom refund fee ({refundFeePercent}% + {refundFlatFee} {openBankingRefundFee.flatFeeCurrency})</span>
            <span style={{ fontWeight: 600 }}>{openBankingFeeAmount.toFixed(2)} {props.checkoutSession?.currency}</span>
          </div>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>
            <span>Total</span>
            <span style={{ fontWeight: 600 }}>
              {openBankingTotalAmount.toFixed(2)} {props.checkoutSession?.currency}
            </span>
          </div>
        </div>
      </div>
    }

    <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_MED, margin: '10px 0' }} />

    <div style={{
      display: 'flex',
      color: RADOM_COLORS.GRAY_DARKEST,
      flexDirection: 'column'
    }}>
      <div style={{
        alignItems: 'center',
        display: 'flex',
        border: `1px solid ${RADOM_COLORS.ORANGE_LIGHT}`,
        borderRadius: 10,
        gap: 15,
        borderBottom: `1px solid ${RADOM_COLORS.ORANGE_LIGHT}`,
        backgroundColor: RADOM_COLORS.ORANGE_LIGHTEST,
        padding: 20,
        borderBottomLeftRadius: props.checkoutSession?.payment?.openBanking ? 0 : 10,
        borderBottomRightRadius: props.checkoutSession?.payment?.openBanking ? 0 : 10

      }}>
        <PaymentMethodDisplay method={{ network: payment.network, token: payment.token }} excludeName />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span>
            <span
              style={{
                fontWeight: 600
              }}
            >
              {props.checkoutSession?.payment?.openBanking
                ? openBankingTotalAmount.toFixed(2)
                : trimZeros(refundAmount)}
            </span>{' '}
            <span style={{ fontWeight: 600 }}>
              {currency} {!chargeCustomerNetworkFee && '+ gas fee'}
            </span>{' '}
            {props.checkoutSession?.payment?.openBanking
              ? (
                <>will be converted to <span style={{ fontWeight: 600 }}>USDC</span> and </>
              )
              : (
                'will be '
              )}
              withdrawn from your balance
          </span>
        </div>
      </div>
      {
        props.checkoutSession?.payment?.openBanking &&
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          fontSize: 14,
          padding: 20,
          border: `1px solid ${RADOM_COLORS.GRAY9}`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTop: 0
        }}>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>

            <span style={{}}>Open banking withdrawal fee</span>
            <span style={{ fontWeight: 600 }}>
              {refundFlatFee} {openBankingRefundFee.flatFeeCurrency}  + {refundFeePercent}%
            </span>
          </div>

          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>

            <span style={{}}>Refunded to contact</span>
            <span style={{ fontWeight: 600 }}>{refundAmount} {props.checkoutSession?.currency}</span>
          </div>
        </div>
      }
    </div>

    {
      requiresPayerAddress &&
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Payer address</InputLabel>
          <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST }}>
            This contact&apos;s bank is outside the EEA, so SEPA requires their address to deliver the refund.
          </span>
          <span style={{ fontSize: 14 }}>
            Refund destination:{' '}
            <span style={{ fontWeight: 600 }}>
              {[senderName, senderIban].filter(Boolean).join(' · ')}
            </span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <TextInputWithLabel
            style={{ flex: 1 }}
            label='City'
            required
            disabled={isLoading}
            value={payerCity}
            onChange={e => setPayerCity((e.target as HTMLInputElement).value)} />
          <TextInputWithLabel
            style={{ flex: 1 }}
            label='Country'
            placeholder='2-letter code, e.g. GB'
            maxLength={2}
            required
            disabled={isLoading}
            value={payerCountry}
            onChange={e => setPayerCountry((e.target as HTMLInputElement).value.toUpperCase())} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <TextInputWithLabel
            style={{ flex: 1 }}
            label='Address line 1'
            disabled={isLoading}
            value={payerAddressLine1}
            onChange={e => setPayerAddressLine1((e.target as HTMLInputElement).value)} />
          <TextInputWithLabel
            style={{ flex: 1 }}
            label='Postcode'
            disabled={isLoading}
            value={payerPostalCode}
            onChange={e => setPayerPostalCode((e.target as HTMLInputElement).value)} />
        </div>
      </div>
    }

    {
      isUnsupportedOpenBankingCurrency &&
      <span style={{ fontSize: 14, color: RADOM_COLORS.ERROR }}>
        Open banking refunds in {currency} are no longer supported.
      </span>
    }

    <PrimaryButton
      style={{ width: '100%' }}
      onClick={submitRefund}
      isLoading={isLoading}
      disabled={isUnsupportedOpenBankingCurrency || (requiresPayerAddress && !isPayerAddressValid)}>
      Issue refund
    </PrimaryButton>
  </div>
}

export default RefundForm
